import { usePermissionStore } from '../stores/permission.store';
import type { Permission } from '../stores/permission.store';

/**
 * Permissions hook
 * Provides permission checking utilities
 */
export function usePermissions() {
  const store = usePermissionStore();

  return {
    // State
    permissions: store.permissions,
    isLoaded: store.isLoaded,

    // Permission checks
    hasPermission: (permission: Permission) => store.hasPermission(permission),
    hasAnyPermission: (permissions: Permission[]) => store.hasAnyPermission(permissions),
    hasAllPermissions: (permissions: Permission[]) => store.hasAllPermissions(permissions),

    // Utility to check if user can perform action
    can: (permission: Permission) => store.hasPermission(permission),
  };
}

/**
 * Imperative permission check hook.
 *
 * Returns a boolean — handy for conditional logic, disabling buttons, filtering columns, etc.
 *
 * Usage:
 *   const canCreate = useCan('iam:user:create');
 *   const canManage = useCan(['iam:user:update', 'iam:user:delete']); // any
 *   const canManageAll = useCan(['iam:user:update', 'iam:user:delete'], true); // all
 */
export function useCan(permission: Permission | Permission[], requireAll = false): boolean {
  const store = usePermissionStore();
  const permissions = Array.isArray(permission) ? permission : [permission];

  if (requireAll) return store.hasAllPermissions(permissions);
  return permissions.length === 1
    ? store.hasPermission(permissions[0]!)
    : store.hasAnyPermission(permissions);
}
